import React from "react";
import {
  Container,
  Paper,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Box,
  Tooltip,
  TextField
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import swal from "sweetalert";
import * as gkApi from "../../api/gkApi";

const GK_FIELD_NAMES = [
  "Orgnr",                // 0
  "Year",                 // 1
  "Quarter",              // 2
  "Country",              // 3
  "QtyPerson",            // 4
  "QtyMaterial",          // 5
  "CompensationPerson",   // 6
  "CompensationMaterial", // 7
  "ReservesPerson",       // 8
  "ReservesMaterial",     // 9
];

class GKImport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      importResult: null
    };
  }

  handleFileChange = (acceptedFiles) => {
    this.setState({
      files: acceptedFiles,
      selectedFileName: acceptedFiles[0]?.name || "",
    });
  };

  handleUpload = () => {
    this.runImport("file");
  };

  handleReuploadChanges = () => {
    this.runImport("changes");
  };

  runImport = async (mode) => {
    const isReupload = mode === "changes" || mode === "confirm-changes";
    const isConfirm = mode === "confirm-file" || mode === "confirm-changes";

    try {
      let res = null;

      if (!isReupload) {
        const { files } = this.state;

        if (!files.length) {
          toast.error("Velg en fil..");
          return;
        }
        if (isConfirm) {
          res = await gkApi.gkConfirmImport(files);
        } else {
          res = await gkApi.gkImport(files);
        }
      } else {
        const { importResult } = this.state;

        if (!importResult || !importResult.rows) {
          toast.error("Ingen data å laste opp.");
          return;
        }

        const header =
          "Orgnr;År;Kvartal;Land;AntallPerson;AntallMateriell;ErstatningPerson;ErstatningMateriell;ReservePerson;ReserveMateriell";

        const rows = importResult.rows.map((row) => {
          const fields = row.fields || [];
          const values = GK_FIELD_NAMES.map((_, idx) => {
            const f = fields[idx];
            return f && f.value != null ? String(f.value).trim() : "";
          });
          return values.join(";");
        });

        const text = [header, ...rows].join("\n");

        if (isConfirm) {
          res = await gkApi.gkConfirmImportFromText(text);
        } else {
          res = await gkApi.gkImportFromText(text);
        }
      }

      if (!res) {
        if (!isReupload) this.setState({ importResult: null });

        swal({
          title: "Opplastingen er mislykket!",
          text: "Teknisk feil. Kontakt administrator.",
          icon: "error",
          dangerMode: true,
        });
        return;
      }

      const { statusCode, ok, data } = res;

      if (statusCode === 401) {
        if (!isReupload) this.setState({ importResult: null });

        swal({
          title: "Ikke autorisert",
          text: "Logg inn på nytt.",
          icon: "warning",
        });
        return;
      }

      if (ok && statusCode === 200) {

        if (data && data.dataExists && !isConfirm) {

          const willOverwrite = await swal({
            title: "Eksisterende data funnet",
            text:
              "Det finnes allerede data for dette selskapet, året og kvartalet. " +
              "Hvis du fortsetter vil eksisterende rader bli slettet og erstattet med de nye.",
            icon: "warning",
            buttons: ["Avbryt", "Overskriv"],
            dangerMode: true,
          });

          if (!willOverwrite) {
            return;
          }

          const confirmMode = isReupload ? "confirm-changes" : "confirm-file";
          await this.runImport(confirmMode);
          return;
        }

        this.setState({ importResult: null });

        swal({
          title: "Opplastingen er vellykket!",
          icon: "success",
        });
        return;
      }

      if (statusCode === 400) {
        this.setState({ importResult: data });

        swal({
          title: "Feil ved opplastning",
          text: isReupload
            ? "Filen inneholder fortsatt valideringsfeil. Se detaljer under."
            : "Filen inneholder valideringsfeil. Se detaljer under.",
          icon: "warning",
        });
        return;
      }

      if (!isReupload) this.setState({ importResult: null });

      swal({
        title: "Opplastingen er mislykket!",
        text: "Teknisk feil. Kontakt administrator.",
        icon: "error",
        dangerMode: true,
      });

    } catch (error) {
      const statusCode = error?.response?.status;
      const data = error?.response?.data;

      if (statusCode === 400 && data) {
        this.setState({ importResult: data });

        swal({
          title: "Feil ved opplastning",
          text: isReupload
            ? "Filen inneholder fortsatt valideringsfeil. Se detaljer under."
            : "Filen inneholder valideringsfeil. Se detaljer under.",
          icon: "warning",
        });
      } else {
        console.error(error);
        if (!isReupload) this.setState({ importResult: null });

        swal({
          title: "Opplastingen er mislykket!",
          text: "Teknisk feil. Kontakt administrator.",
          icon: "error",
          dangerMode: true,
        });
      }
    } finally {
      if (!isReupload) {
        this.setState({ files: [], selectedFileName: "" });
      }
    }
  };

  handleCellChange = (rowIndex, fieldIndex, newValue) => {
    this.setState((prev) => {
      if (!prev.importResult) return null;

      const importResult = { ...prev.importResult };
      const rows = [...importResult.rows];
      const row = { ...rows[rowIndex] };
      const fields = [...(row.fields || [])];

      const existingField = fields[fieldIndex];

      if (existingField) {
        fields[fieldIndex] = {
          ...existingField,
          value: newValue,
        };
      } else {
        fields[fieldIndex] = {
          fieldName: GK_FIELD_NAMES[fieldIndex] || `Field${fieldIndex}`,
          value: newValue,
          hasError: false,
          messages: [],
        };
      }

      row.fields = fields;
      rows[rowIndex] = row;
      importResult.rows = rows;

      return { importResult };
    });
  };

  render() {
    const headCellStyle = {
      backgroundColor: "#e6e6e6",
      fontWeight: "bold",
      fontSize: 14,
      minWidth: 120,
      textAlign: "center",

      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: 1.2,
      padding: "4px 8px",
    };


    const COLUMN_WIDTHS = {
      Rad: 60,
      Orgnr: 110,
      År: 80,
      Kvartal: 80,
      Land: 150,
      Default: 120,
    };

    const { importResult } = this.state;

    return (
      <Container maxWidth="xl">
        <main style={{ width: "100%", paddingTop: 120, margin: "auto" }}>
          <Paper style={{ width: "100%", padding: "20px" }}>
            <Grid container spacing={3} style={{ marginLeft: 10, marginRight: 10, display: "flex", flexDirection: "column" }}>
              <Grid item>
                <Typography variant="h4" sx={{ mb: 3 }}>
                  Dataimport GK:
                </Typography>
              </Grid>

              <Grid item>
                <Box sx={{ width: "96%" }}>
                  <Dropzone onDrop={this.handleFileChange} />
                </Box>
              </Grid>

              <Grid item sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CloudDownloadIcon />}
                  onClick={this.handleUpload}
                >
                  Last opp fil
                </Button>
              </Grid>

              {importResult && (
                <Grid item lg={12} sx={{ mt: 4, width: "96%" }}>


                  {importResult && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={this.handleReuploadChanges}
                    >
                      Last opp med endringer
                    </Button>
                  )}

                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {`Totalt ${importResult.totalRowsOfData} rader, ${importResult.successfulRows} rader er importert`}
                  </Typography>

                  {importResult.status !== 0 && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      Importen inneholder feil. Rader markert i rødt har problemer.
                    </Typography>
                  )}

                  <ScrollSync>
                    <TableContainer
                      component={Paper}
                      sx={{ mt: 1 }}
                    >
                      <Table
                        size="small"
                        sx={{
                          minWidth: 1500,
                          tableLayout: "fixed",
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Rad }}>Rad</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Orgnr }}>Orgnr</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.År }}>År</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Kvartal }}>Kvartal</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Land }}>Land</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Default }}>Antall Person</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Default }}>Antall Materiell</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Default }}>Erstatning Person</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Default }}>Erstatning Materiell</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Default }}>Reserve Person</TableCell>
                            <TableCell style={{ ...headCellStyle, minWidth: COLUMN_WIDTHS.Default }}>Reserve Materiell</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResult.rows?.map((row, rowIndex) => {
                            const rowMessages = row.rowMessages || [];
                            const hasRowError = row.rowHasError || rowMessages.length > 0;
                            const rowTooltipText = rowMessages.join(" ");

                            const hasFieldErrors = row.fields?.some((f) => f.hasError) ?? false;
                            const rowBaseStyle = hasRowError ? { backgroundColor: "#ffebee" } : {};

                            const totalFieldCount = GK_FIELD_NAMES.length;
                            const rowCells = [];

                            // First column: Rad (not editable)
                            rowCells.push(
                              <TableCell key="rowNumber" style={rowBaseStyle}>
                                {row.rowNumber}
                              </TableCell>
                            );

                            const fields = row.fields || [];

                            for (let i = 0; i < totalFieldCount; i++) {
                              const field = fields[i];
                              const isMissingField = !field;

                              const cellHasError = isMissingField
                                ? hasRowError // missing col => treat as row error
                                : field.hasError;

                              const cellMessages =
                                !isMissingField && field.messages && field.messages.length > 0
                                  ? field.messages
                                  : [];

                              const cellTooltipText =
                                cellHasError && cellMessages.length > 0
                                  ? cellMessages.join(" ")
                                  : isMissingField && hasRowError && rowTooltipText
                                    ? rowTooltipText
                                    : "";

                              const cellStyle = cellHasError
                                ? {
                                  backgroundColor: "#ffcdd2",
                                  border: "2px solid #d32f2f",
                                  boxSizing: "border-box",
                                }
                                : {};

                              const value = isMissingField ? "" : field.value ?? "";

                              const cellContent = (
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  value={value}
                                  onChange={(e) =>
                                    this.handleCellChange(rowIndex, i, e.target.value)
                                  }
                                  fullWidth
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      minHeight: 32,
                                      paddingX: 0.5,
                                    },
                                    "& .MuiInputBase-input": {
                                      fontSize: 13,
                                      paddingY: 0.5,
                                    },
                                  }}
                                />
                              );

                              const cellNode = (
                                <TableCell
                                  key={i}
                                  style={cellStyle}
                                  sx={{ p: 0.5 }}
                                >
                                  {cellContent}
                                </TableCell>
                              );

                              rowCells.push(
                                cellTooltipText ? (
                                  <Tooltip
                                    key={`tooltip-${row.rowNumber}-${i}`}
                                    title={cellTooltipText}
                                    componentsProps={{
                                      tooltip: { sx: { fontSize: 16 } },
                                    }}
                                    arrow
                                  >
                                    {cellNode}
                                  </Tooltip>
                                ) : (
                                  cellNode
                                )
                              );
                            }

                            const rowElement = (
                              <TableRow key={row.rowNumber} style={rowBaseStyle}>
                                {rowCells}
                              </TableRow>
                            );

                            const showRowTooltip =
                              hasRowError && rowMessages.length > 0 && !hasFieldErrors;

                            return showRowTooltip ? (
                              <Tooltip
                                key={`row-tooltip-${row.rowNumber}`}
                                title={rowTooltipText}
                                componentsProps={{
                                  tooltip: { sx: { fontSize: 16 } },
                                }}
                                arrow
                              >
                                {rowElement}
                              </Tooltip>
                            ) : (
                              rowElement
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </ScrollSync>
                  <br />
                  {importResult && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={this.handleReuploadChanges}
                    >
                      Last opp med endringer
                    </Button>
                  )}
                </Grid>
              )}

            </Grid>
          </Paper>
        </main>
      </Container>
    );
  }
}

const ScrollSync = ({ children }) => {
  const topRef = React.useRef(null);
  const bottomRef = React.useRef(null);
  const [contentWidth, setContentWidth] = React.useState(0);

  const child = React.Children.only(children);

  React.useLayoutEffect(() => {
    if (bottomRef.current) {
      setContentWidth(bottomRef.current.scrollWidth);
    }
  }, [children]);

  const handleTopScroll = () => {
    if (!topRef.current || !bottomRef.current) return;
    bottomRef.current.scrollLeft = topRef.current.scrollLeft;
  };

  const handleBottomScroll = () => {
    if (!topRef.current || !bottomRef.current) return;
    topRef.current.scrollLeft = bottomRef.current.scrollLeft;
  };

  return (
    <Box>
      <Box
        ref={topRef}
        sx={{
          overflowX: "auto",
          mb: 1,
          height: 16,
        }}
        onScroll={handleTopScroll}
      >
        <Box sx={{ width: contentWidth || "100%" }} />
      </Box>

      {React.cloneElement(child, {
        ref: bottomRef,
        onScroll: (e) => {
          handleBottomScroll();
          if (child.props.onScroll) {
            child.props.onScroll(e);
          }
        },
        sx: {
          ...(child.props.sx || {}),
          overflowX: "auto",
        },
      })}
    </Box>
  );
};


function Dropzone({ onDrop }) {
  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    onDrop,
    accept: [".xls", ".xlsx", ".txt", ".csv"],
    maxFiles: 1,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed #aaa",
        padding: 4,
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <input {...getInputProps()} />
      <Typography variant="body1">
        Dra og slipp en fil her eller klikk for å velge en fil
      </Typography>
      {acceptedFiles.length > 0 && (
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Valgt fil: {acceptedFiles[0].name}
        </Typography>
      )}
    </Box>
  );
}

export default GKImport;
